/**
 * @module
 *
 * @import {Vote} from '../service/votes';
 */

import {AttributeLink, AttributeSetup, task} from '@acng/frontend-stargazer';
import {GlobalActor, globalValue, Watch} from '@acng/frontend-relativity';
import {ARRAY, guard, STRING} from '@acng/frontend-rubicon';
import {iso, now} from '@acng/frontend-bounty/std/date.js';
import {HIDDEN, set, unset} from '@acng/frontend-bounty/dom/attribute.js';
import {remove} from '@acng/frontend-bounty/dom/move.js';
import {all} from '@acng/frontend-bounty/std/control.js';
import {attributeSelector} from '@acng/frontend-bounty/dom/query.js';
import {clearTimeout, setTimeout} from '@acng/frontend-bounty';

import {TIMING_END, TIMING_START} from '../../layout/attribute/common.js';
import {cdn} from '../../core/service/cdn.js';
import {user} from '../../user/user.js';
import {ctxVotables, ctxVotes} from '../context/list.js';
import {setup} from '../service/votes.js';
import {feature} from '../service/template.js';
import {inject, isInjectable} from 'acng/core/service/ng.js';
import {onlineFirst} from 'acng/amateurPool/service/online.js';
import {HOTORNOT_TIMER_EXPIRE, HOTORNOT_LIST, localStore} from 'acng/api/browser-storage.js';

/**
 * @group DOM Attribute
 */
const name = 'hotornot-timer';

AttributeSetup(name, () => {
  const actionslider = isInjectable('Actionslider') ? inject('Actionslider') : null;
  let global = localStore[HOTORNOT_LIST] ?? [];
  let expire = localStore[HOTORNOT_TIMER_EXPIRE] ?? now();
  const lifetime = 6e4 * 10;
  const provideVotables = GlobalActor(ctxVotables);
  const watchVotes = Watch(attributeSelector(name), ctxVotes);

  const reload = async () => {
    expire = now() + lifetime;
    const url = `/api/hotornot/votables?fsk=${user.fsk}&niche=${user.niche}&sorting_suffix=${user.list_sorting_suffix}`;
    const [list, votes] = await all([cdn(url), setup()]);

    ASSERT: guard(list, ARRAY(STRING), `${url} response`);
    global = filter(await onlineFirst(list), votes);
    localStore[HOTORNOT_LIST] = global;
    localStore[HOTORNOT_TIMER_EXPIRE] = expire;
  };

  /**
   * @param {Amateur['id'][]} list
   * @param {Vote[]} votes
   */
  const filter = (list, votes) =>
    list.filter((id) => !votes.some((vote) => vote.amateur_id == parseInt(id))).slice(0, 10);

  AttributeLink(name, () => (element) => {
    /**
     * @param {Element} element
     * @param {Vote[]} votes
     */
    const update = (element, votes) => {
      const list = filter(global, votes);

      if (list.length) {
        unset(element, TIMING_END);
      } else {
        set(element, TIMING_END, iso(expire));
        set(element, TIMING_START, '0');
      }

      provideVotables(list);
    };

    const startTimeout = () =>
      setTimeout(async () => {
        await reload();
        await update(element, globalValue(ctxVotes));
        timeout = startTimeout();
      }, expire - now());

    let timeout = startTimeout();

    watchVotes(element, update);
    actionslider?.hide();

    return () => {
      actionslider?.show();
      clearTimeout(timeout);
    };
  });

  return (element, value, compiler) => {
    if (feature[HIDDEN]) {
      remove(element);
    } else if (expire <= now()) {
      task(compiler, reload());
    }
  };
});

export {name as HOTORNOT_TIMER};
